import { KeyOn } from '@tableau/vizportalclient-common-components';
import { InlineAlertType, InlineAlertWidget, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { HelpService } from '~src/commonInfra/services/HelpService';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';

import { Messages } from '../../codegen/Localize';
import { ServerUsers } from '../../userManagement/services/resources/ServerUsers';
import { IServerUser } from '../../userManagement/users/serverApi/types/IServerUser';
import { UserSettingsStyle } from '../../userSettings/styles/UserSettingsStyle';
import { IPersonalAccessToken } from '../serverApi/types/personalAccess/IPersonalAccessToken';
import { CreatePersonalAccessTokensSection } from './CreatePersonalAccessTokenSection';
import { ListPersonalAccessTokensSection } from './ListPersonalAccessTokenSection';

export interface PersonalAccessTokensSectionProps {
  canCreatePersonalAccessTokens: boolean;
  serverUser: IServerUser;
}

function PersonalAccessTokensSectionFC(props: PersonalAccessTokensSectionProps) {
  const [personalAccessTokens, setPersonalAccessTokens] = React.useState([] as IPersonalAccessToken[]);
  const [personalAccessTokenCreationAllowed, setPersonalAccessTokenCreationAllowed] = React.useState(Boolean);

  async function refreshPersonalAccessTokens(userId: string): Promise<IPersonalAccessToken[]> {
    const tokens = await ServerUsers.instance.getPersonalAccessTokens(userId);
    setPersonalAccessTokens(tokens);
    return tokens;
  }

  async function canCreatePersonalAccessTokensBasedOnSiteSettings() {
    const result = await ServerUsers.instance.checkPersonalAccessTokenCreationIsAllowed();
    if (!result.errors) {
      setPersonalAccessTokenCreationAllowed(true);
    } else {
      // For now, regardless of the error code, disallow pat creation if any error
      setPersonalAccessTokenCreationAllowed(false);
    }
  }

  function getPatCreateSection() {
    if (!props.canCreatePersonalAccessTokens) {
      return <></>;
    }

    var sectionValue = <></>;

    if (personalAccessTokenCreationAllowed) {
      sectionValue = <CreatePersonalAccessTokensSection
        personalAccessTokens={personalAccessTokens}
        refreshTokenList={() => refreshPersonalAccessTokens(props.serverUser.id)} />;
    } else if (personalAccessTokens.length === 0) {
      // User can't create PAT and has no PAT to start with
      sectionValue = <InlineAlertWidget testId={'pat-creation-message'} type={InlineAlertType.Info}>
        {Messages.personalAccessTokensCannotCreateInfo()}
      </InlineAlertWidget>;
    } else {
      // User can't create PAT now but had PAT's created earlier
      sectionValue = <InlineAlertWidget testId={'pat-creation-warning'} type={InlineAlertType.Warning}>
        {Messages.personalAccessTokensCannotCreateWarning()}
      </InlineAlertWidget>;
    }

    return <tr>
      <td className={UserSettingsStyle.labelText} />
      <td className={UserSettingsStyle.detailsValue}>
        {sectionValue}
      </td>
    </tr>;
  }

  React.useEffect(() => {
    setPersonalAccessTokens([]);
    refreshPersonalAccessTokens(props.serverUser.id);
    setPersonalAccessTokenCreationAllowed(true);
    canCreatePersonalAccessTokensBasedOnSiteSettings();
  }, [props.serverUser.id]);

  return (
    <table>
      <tbody data-tb-test-id="personal-access-token-section">
        <tr>
          <td className={UserSettingsStyle.labelText}>
            {Messages.personalAccessTokenSection()}
          </td>
          <td className={UserSettingsStyle.detailsValue}>
            {Messages.personalAccessTokenDescription()}
          &nbsp;
          <TextLinkWidget
            testId={'allow-pat-creation-description'}
            target={'_blank'}
            className={learnMoreStyle}
            href={HelpService.getServerHelpUrlByCategory('personal-access-token')}>
            {Messages.learnMore()}
          </TextLinkWidget>
          </td>
        </tr>
          {getPatCreateSection()}
        <tr>
          <td className={UserSettingsStyle.labelText} />
          <td className={UserSettingsStyle.detailsValue}>
            <ListPersonalAccessTokensSection
              serverUser={props.serverUser}
              personalAccessTokens={personalAccessTokens}
              refreshTokenList={refreshPersonalAccessTokens} />
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export const PersonalAccessTokensSection = KeyOn(
  PersonalAccessTokensSectionFC,
  (props: PersonalAccessTokensSectionProps) => props.serverUser.id);
